import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, Film, Mail, Briefcase, Settings as Cog, UploadCloud } from "lucide-react";

const TABS = [
  { id: "highlights", label: "Highlights", icon: Film },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "inquiries", label: "Inquiries", icon: Briefcase },
  { id: "settings", label: "Settings", icon: Cog },
];

const EMPTY_HL = { title: "", category: "Season Highlights", embed_url: "", thumbnail: "", description: "", order: 0, platform: "", orientation: "", video_webm: "" };
const DEFAULT_CATS = ["Season Highlights", "Top Plays", "Game Winners", "Training", "The Overseas Diaries", "Mic'd Up"];

function HighlightsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_HL);
  const [uploading, setUploading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const load = () => api.get("/admin/highlights").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/highlights/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setForm((f) => ({
        ...f,
        embed_url: data.url,
        platform: "video",
        orientation: data.orientation || "landscape",
        thumbnail: f.thumbnail || data.thumbnail || "",
      }));
      toast.success("Video uploaded & processed");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) await api.put(`/admin/highlights/${editing}`, payload);
      else await api.post("/admin/highlights", payload);
      toast.success("Saved");
      setForm(EMPTY_HL); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const edit = (h) => { setEditing(h.id); setForm({ ...EMPTY_HL, ...h }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/highlights/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, thumbnail: data.url }));
      toast.success("Cover photo uploaded");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };

  const catOptions = Array.from(new Set([...DEFAULT_CATS, ...items.map((i) => i.category).filter(Boolean)]));

  const del = async (id) => {
    if (!window.confirm("Delete this highlight?")) return;
    await api.delete(`/admin/highlights/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={save} className="border border-silver/15 bg-charcoal/30 p-6 space-y-4 h-fit">
        <div className="flex items-center justify-between">
          <h3 className="heading text-2xl text-cream">{editing ? "Edit" : "Add"} Highlight</h3>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_HL); }} className="text-silver/60 hover:text-cream">
              <X size={18} />
            </button>
          )}
        </div>
        <input data-testid="hl-title" required placeholder="Title" value={form.title} onChange={set("title")} className="field" />
        <input
          data-testid="hl-category"
          list="hl-cat-list"
          required
          placeholder="Category (pick one or type a new one)"
          value={form.category}
          onChange={set("category")}
          className="field"
        />
        <datalist id="hl-cat-list">
          {catOptions.map((c) => <option key={c} value={c} />)}
        </datalist>
        <input data-testid="hl-embed" required placeholder="Paste a YouTube / Instagram / TikTok link, or upload a file below" value={form.embed_url} onChange={set("embed_url")} className="field" />

        <div className="border border-dashed border-silver/25 p-4 text-center">
          <p className="text-silver/50 text-xs mb-3 uppercase tracking-[0.15em]">or upload a video file</p>
          <label data-testid="hl-upload-label" className={`inline-flex items-center gap-2 cursor-pointer btn-secondary !py-2.5 !px-5 ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            <UploadCloud size={16} />
            {uploading ? "Processing…" : "Choose Video"}
            <input data-testid="hl-upload-input" type="file" accept="video/*" onChange={uploadVideo} className="hidden" disabled={uploading} />
          </label>
          {form.platform === "video" && form.embed_url && (
            <p data-testid="hl-upload-done" className="text-green-400 text-xs mt-3 break-all">✓ Video ready ({form.orientation}): {form.embed_url}</p>
          )}
          <p className="text-silver/40 text-[0.65rem] mt-2">Any format (mov, mp4…). It's auto-converted for web playback.</p>
        </div>

        <div className="border border-silver/15 p-4">
          <p className="text-silver/50 text-xs mb-3 uppercase tracking-[0.15em]">Cover photo</p>
          <div className="flex items-center gap-4">
            {form.thumbnail ? (
              <img data-testid="hl-cover-preview" src={form.thumbnail} alt="cover" className="w-24 h-16 object-cover border border-silver/20" />
            ) : (
              <div className="w-24 h-16 border border-dashed border-silver/25 flex items-center justify-center text-silver/40 text-[0.6rem]">No cover</div>
            )}
            <div className="flex flex-col gap-2">
              <label data-testid="hl-cover-label" className={`inline-flex items-center gap-2 cursor-pointer btn-secondary !py-2.5 !px-5 ${uploadingImg ? "opacity-60 pointer-events-none" : ""}`}>
                <UploadCloud size={16} />
                {uploadingImg ? "Uploading…" : form.thumbnail ? "Replace Cover" : "Upload Cover"}
                <input data-testid="hl-cover-input" type="file" accept="image/*" onChange={uploadImage} className="hidden" disabled={uploadingImg} />
              </label>
              {form.thumbnail && (
                <button type="button" data-testid="hl-cover-clear" onClick={() => setForm((f) => ({ ...f, thumbnail: "" }))} className="text-silver/50 hover:text-red-400 text-xs uppercase tracking-[0.15em] text-left">
                  Remove cover
                </button>
              )}
            </div>
          </div>
        </div>

        <input data-testid="hl-desc" placeholder="Short description" value={form.description} onChange={set("description")} className="field" />
        <input data-testid="hl-order" type="number" placeholder="Order" value={form.order} onChange={set("order")} className="field" />
        <button data-testid="hl-save" className="btn-primary w-full"><Plus size={16} /> {editing ? "Update" : "Add"} Highlight</button>
      </form>

      <div className="space-y-3">
        {items.map((h) => (
          <div key={h.id} data-testid={`admin-hl-${h.id}`} className="flex items-center gap-4 border border-silver/15 bg-charcoal/20 p-3">
            {h.thumbnail && <img src={h.thumbnail} alt="" className="w-20 h-14 object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-cream truncate">{h.title}</p>
              <p className="text-silver/50 text-xs">{h.category}</p>
            </div>
            <button onClick={() => edit(h)} className="text-silver/60 hover:text-stone p-2"><Pencil size={16} /></button>
            <button data-testid={`hl-del-${h.id}`} onClick={() => del(h.id)} className="text-silver/60 hover:text-red-400 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-silver/40">No highlights yet.</p>}
      </div>
    </div>
  );
}

function ListTab({ endpoint, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)); }, [endpoint]);
  return (
    <div data-testid={testid} className="border border-silver/15 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-charcoal/40 text-left">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 uppercase tracking-[0.15em] text-[0.65rem] text-silver/60">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-silver/10">
              {columns.map((c) => <td key={c.key} className="px-4 py-3 text-cream/80 align-top">{c.fmt ? c.fmt(r[c.key]) : (r[c.key] || "-")}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-silver/40">No records yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };
  const testShopify = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/shopify/test", s);
      setTestResult(data);
      if (data.ok) toast.success(`Connected: ${data.product_count} products found`);
      else toast.error("Connection failed");
    } catch {
      setTestResult({ ok: false, error: "Request failed" });
    } finally { setTesting(false); }
  };
  if (!s) return <p className="text-silver/40">Loading…</p>;
  return (
    <form onSubmit={save} className="max-w-2xl space-y-10">
      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Shopify Integration</h3>
        <p className="text-silver/50 text-xs">Connect your Shopify store via the Storefront API. Enter your store domain + a <strong>Storefront access token</strong> (Shopify admin → Settings → Apps and sales channels → Develop apps → your app → Storefront API). When connected, the Shop section pulls your live products automatically.</p>
        <label className="flex items-center gap-3 text-cream/80">
          <input data-testid="set-shopify-enabled" type="checkbox" checked={s.shopify_enabled} onChange={set("shopify_enabled")} /> Enable Shopify store (show live products)
        </label>
        <input data-testid="set-shopify-domain" placeholder="Store domain (e.g. your-store.myshopify.com)" value={s.shopify_domain} onChange={set("shopify_domain")} className="field" />
        <input data-testid="set-shopify-token" placeholder="Storefront API access token" value={s.shopify_storefront_token} onChange={set("shopify_storefront_token")} className="field" />
        <input data-testid="set-shopify-handle" placeholder="Collection handle (optional, e.g. one-of-none)" value={s.shopify_collection_handle} onChange={set("shopify_collection_handle")} className="field" />
        <input data-testid="set-shopify-collection" placeholder="'Shop All' link URL (optional)" value={s.shopify_collection_url} onChange={set("shopify_collection_url")} className="field" />
        <div className="flex items-center gap-4 pt-1">
          <button type="button" data-testid="shopify-test-btn" onClick={testShopify} disabled={testing} className="btn-secondary !py-3 !px-6">
            {testing ? "Testing…" : "Test Connection"}
          </button>
          {testResult && (
            <span data-testid="shopify-test-result" className={`text-sm ${testResult.ok ? "text-green-400" : "text-red-400"}`}>
              {testResult.ok ? `✓ ${testResult.product_count} products found` : `✕ ${testResult.error}`}
            </span>
          )}
        </div>
        <p className="text-silver/40 text-xs">Tip: click Save Settings first, then Test Connection uses the values in the form.</p>
      </div>

      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Tracking & Tags</h3>
        <p className="text-silver/50 text-xs">Add analytics & marketing tags. They're injected site-wide automatically.</p>
        <input data-testid="set-ga" placeholder="Google Analytics ID (G-XXXXXXX)" value={s.google_analytics_id} onChange={set("google_analytics_id")} className="field" />
        <input data-testid="set-gtm" placeholder="Google Tag Manager ID (GTM-XXXXXX)" value={s.google_tag_manager_id} onChange={set("google_tag_manager_id")} className="field" />
        <input data-testid="set-pixel" placeholder="Meta / Facebook Pixel ID" value={s.meta_pixel_id} onChange={set("meta_pixel_id")} className="field" />
      </div>

      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Social & Share Tags</h3>
        <p className="text-silver/50 text-xs">Open Graph / Twitter card data used when the site is shared.</p>
        <input data-testid="set-og-title" placeholder="Share title (og:title)" value={s.og_title} onChange={set("og_title")} className="field" />
        <input data-testid="set-og-desc" placeholder="Share description (og:description)" value={s.og_description} onChange={set("og_description")} className="field" />
        <input data-testid="set-og-image" placeholder="Share image URL (og:image)" value={s.og_image} onChange={set("og_image")} className="field" />
      </div>

      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Social Links</h3>
        <input data-testid="set-instagram" placeholder="Instagram URL" value={s.instagram_url} onChange={set("instagram_url")} className="field" />
        <input data-testid="set-tiktok" placeholder="TikTok URL" value={s.tiktok_url} onChange={set("tiktok_url")} className="field" />
        <input data-testid="set-youtube" placeholder="YouTube URL" value={s.youtube_url} onChange={set("youtube_url")} className="field" />
        <input data-testid="set-x" placeholder="X (Twitter) URL" value={s.x_url} onChange={set("x_url")} className="field" />
        <input data-testid="set-contact-email" placeholder="Contact email" value={s.contact_email} onChange={set("contact_email")} className="field" />
      </div>

      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Schedule</h3>
        <label className="flex items-center gap-3 text-cream/80">
          <input data-testid="set-offseason" type="checkbox" checked={s.schedule_offseason} onChange={set("schedule_offseason")} /> Offseason mode (no upcoming games)
        </label>
        <input data-testid="set-opponent" placeholder="Next game opponent" value={s.next_game_opponent} onChange={set("next_game_opponent")} className="field" />
        <input data-testid="set-location" placeholder="Location" value={s.next_game_location} onChange={set("next_game_location")} className="field" />
        <input data-testid="set-league" placeholder="League" value={s.next_game_league} onChange={set("next_game_league")} className="field" />
        <div>
          <label className="text-silver/50 text-xs uppercase tracking-[0.2em] block mb-2">Game date & time</label>
          <input data-testid="set-date" type="datetime-local" value={s.next_game_date} onChange={set("next_game_date")} className="field" />
        </div>
      </div>

      <div className="border border-silver/15 bg-charcoal/30 p-6 space-y-4">
        <h3 className="heading text-2xl text-cream">Media Kit</h3>
        <input data-testid="set-mediakit" placeholder="Media kit URL" value={s.media_kit_url} onChange={set("media_kit_url")} className="field" />
      </div>

      <button data-testid="settings-save" className="btn-primary">Save Settings</button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("highlights");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (user === null || user === false) {
    return <div className="min-h-screen bg-matte flex items-center justify-center text-silver/50">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-matte text-cream">
      <header className="border-b border-silver/10 px-6 md:px-10 py-5 flex items-center justify-between">
        <div>
          <span className="heading text-2xl">One Of None</span>
          <span className="font-accent text-stone text-lg ml-2">HQ</span>
        </div>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 text-silver/60 hover:text-cream uppercase tracking-[0.15em] text-xs">
          <LogOut size={16} /> Logout
        </button>
      </header>

      <div className="px-6 md:px-10 py-8">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 uppercase tracking-[0.15em] text-xs border transition-colors ${
                  tab === t.id ? "bg-stone text-matte border-stone" : "border-silver/15 text-cream/70 hover:border-stone/50"
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "highlights" && <HighlightsTab />}
        {tab === "subscribers" && (
          <ListTab
            testid="subscribers-table"
            endpoint="/admin/subscribers"
            columns={[{ key: "email", label: "Email" }, { key: "created_at", label: "Joined", fmt: (v) => new Date(v).toLocaleString() }]}
          />
        )}
        {tab === "inquiries" && (
          <ListTab
            testid="inquiries-table"
            endpoint="/admin/inquiries"
            columns={[
              { key: "name", label: "Name" },
              { key: "company", label: "Company" },
              { key: "email", label: "Email" },
              { key: "partnership_type", label: "Type" },
              { key: "message", label: "Message" },
              { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleDateString() },
            ]}
          />
        )}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
